/**
 * React Query query options for proposals
 *
 * Provides query factories for use with useQuery/useSuspenseQuery
 */

import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import type { ProposalAPIError } from '../core/errors';
import { createProposalClient } from '../core/ProposalClient';
import type { ProposalDetail, ProposalListFilters } from '../core/types';
import { proposalQueryKeys } from './keys';
import { filterResolvingTombstones } from './resolvingTombstones';
import { fetchTriggerData } from './triggerFetcher';

/**
 * Query options for listing proposals
 *
 * @param orgId - Organization ID
 * @param filters - Optional filters
 * @returns Query options for React Query
 *
 * @example
 * ```typescript
 * // In a component:
 * const { data } = useQuery(proposalListQuery(orgId, { resolution: 'pending' }));
 *
 * // With suspense:
 * const { data } = useSuspenseQuery(proposalListQuery(orgId));
 * ```
 */
export function proposalListQuery(
  orgId: number,
  filters?: ProposalListFilters
) {
  const client = createProposalClient(orgId);

  return queryOptions({
    queryKey: proposalQueryKeys.list(orgId, filters),
    // filterResolvingTombstones keeps proposals whose approve/dismiss is
    // in flight OUT of refetched responses — a refetch during that window
    // reads the server's still-pending state and would otherwise resurrect
    // the optimistically-removed row (see resolvingTombstones.ts).
    queryFn: async () => filterResolvingTombstones(await client.list(filters)),
    // Cache list queries for 30 seconds (frequent updates expected)
    staleTime: 30_000,
    gcTime: 5 * 60_000, // Keep in cache for 5 minutes
  });
}

/**
 * Infinite-scroll variant of {@link proposalListQuery}: page number is the
 * page param, `next` from the DRF envelope decides whether another page
 * exists. The key extends the plain list key with an 'infinite' suffix so
 * the two shapes (response vs InfiniteData) never share a cache entry,
 * while prefix-based invalidation of the list key still hits both.
 *
 * @param orgId - Organization ID
 * @param filters - Filters (everything except `page`, which is the param)
 */
export function proposalListInfiniteQuery(
  orgId: number,
  filters: Omit<ProposalListFilters, 'page'>
) {
  const client = createProposalClient(orgId);

  return infiniteQueryOptions({
    queryKey: [...proposalQueryKeys.list(orgId, filters), 'infinite'] as const,
    queryFn: ({ pageParam }) => client.list({ ...filters, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.next ? allPages.length + 1 : undefined,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });
}

/**
 * Query options for getting a single proposal with full details
 *
 * @param orgId - Organization ID
 * @param proposalId - Proposal ID
 * @returns Query options for React Query
 *
 * @example
 * ```typescript
 * const { data: proposal } = useQuery(proposalDetailQuery(orgId, proposalId));
 * console.log(proposal.actions); // Decrypted action payloads
 * ```
 */
export function proposalDetailQuery(orgId: number, proposalId: number) {
  const client = createProposalClient(orgId);

  return queryOptions<
    ProposalDetail,
    ProposalAPIError,
    ProposalDetail,
    ReturnType<typeof proposalQueryKeys.detail>
  >({
    queryKey: proposalQueryKeys.detail(orgId, proposalId),
    queryFn: (): Promise<ProposalDetail> => client.get(proposalId),
    // Cache detail queries for 1 minute
    staleTime: 60_000,
    gcTime: 10 * 60_000, // Keep in cache for 10 minutes
  });
}

/**
 * Query options for pending proposals assigned to the current user
 *
 * @param orgId - Organization ID
 * @returns Query options for React Query
 *
 * @example
 * ```typescript
 * const { data } = useQuery(myPendingProposalsQuery(orgId));
 * ```
 */
export function myPendingProposalsQuery(orgId: number) {
  return proposalListQuery(orgId, {
    resolution: 'pending',
    assigned_to_me: true,
  });
}

/**
 * Query options for proposals by resolution status
 *
 * @param orgId - Organization ID
 * @param resolution - Resolution status
 * @returns Query options for React Query
 *
 * @example
 * ```typescript
 * const { data } = useQuery(proposalsByResolutionQuery(orgId, 'approved'));
 * ```
 */
export function proposalsByResolutionQuery(
  orgId: number,
  resolution: 'pending' | 'approved' | 'rejected' | 'withdrawn'
) {
  return proposalListQuery(orgId, { resolution });
}

/**
 * Query options for proposals by filter tag
 *
 * @param orgId - Organization ID
 * @param tag - Filter tag
 * @returns Query options for React Query
 *
 * @example
 * ```typescript
 * // Get all email proposals
 * const { data } = useQuery(proposalsByTagQuery(orgId, 'email'));
 * ```
 */
export function proposalsByTagQuery(
  orgId: number,
  tag: 'email' | 'pipeline_update' | 'research'
) {
  return proposalListQuery(orgId, { filter_tags__contains: tag });
}

/**
 * Query options for trigger data for a proposal action
 *
 * @param orgId - Organization ID
 * @param proposalId - Proposal ID
 * @param actionId - Action ID
 * @returns Query options for React Query
 *
 * @example
 * ```typescript
 * const { data } = useQuery(triggerQuery(orgId, proposalId, actionId));
 * ```
 */
export function triggerQuery(
  orgId: number,
  proposalId: number,
  actionId: number,
  /**
   * Action id to key the cache under. Defaults to `actionId`; callers editing
   * a versioned action pass the ROOT version's id so the entry survives a
   * version bump. The fetch still uses `actionId` because only the current
   * version resolves server-side. See `getTriggerActionIds`.
   */
  cacheActionId: number = actionId
) {
  return queryOptions({
    queryKey: proposalQueryKeys.trigger(orgId, proposalId, cacheActionId),
    queryFn: () => fetchTriggerData(orgId, proposalId, actionId),
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    retry: 1,
    retryDelay: 1000,
  });
}
